// The full lift record at /office/lifts/:id (Session AC). The Lifts register is a clean scan-only
// list; this is where all the work happens. Every action that used to clutter the list rows lives
// here, organised into labelled sections: lift details, service, LOLER, compliance (SAFed + test
// certificates + photos), RFID tag, and the edit/delete actions. The page loads the enriched asset
// (compliance light + next-service banner) plus each child list, and reloads after any mutation.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import type { ServiceHistoryItem, LolerReportListItem, SafedTestItem, AssetCertificateItem, AssetPhotoHistoryItem, AssetPartItem, DefectListItem } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { assets } from "../../lib/resources";
import { RecordPicker } from "../../components/RecordPicker";
import type { Asset } from "../../lib/resources";
import { valueToLabel } from "./register/RegisterPage";
import { ASSET_KIND_OPTIONS, FREQUENCY, DRIVE_TYPE, NextServiceDueCell, LiftStatusDot, assetTypeLabel } from "./register/AssetsPage";
import { GeneratePpmButton } from "./register/GeneratePpmButton";
import { InsuranceItemsSection } from "./InsuranceItemsSection";
import { LastServicedButton } from "./register/LastServicedButton";
import { SafedTestsButton, safedDueStatus } from "./register/SafedTestsButton";
import { SafedJobsPanel } from "./register/SafedJobsPanel";
import { LogPartButton } from "./register/LogPartButton";
import { RfidTagButton } from "./register/RfidTagButton";
// Session AMZ: the lift's SECOND, INTERNAL tag and the attendance it records.
import { AttendanceTapHistory } from "./AttendanceTapHistory";
import { UploadCertButton } from "./register/UploadCertButton";
import { CertificatesButton } from "./register/CertificatesButton";
import { JobPhotoGallery } from "./JobPhotoGallery";
import { RaiseWorkMenu } from "./RaiseWorkMenu";
import { RaiseSafedButton } from "./RaiseSafedButton";
import { LiftPhotos } from "./LiftPhotos";
import { ServiceRecordInline, DiscardChangesDialog } from "./ServiceRecordPanel";
import { BackdatedCompletionModal } from "./BackdatedCompletionModal";
import JobTypeBadge from "../../components/JobTypeBadge";
import { dueDateTone } from "../../lib/liftStatus";
import { Button } from "../../components/Button";
import { AddLolerReportModal } from "./AddLolerReportModal";
import { RecordHeader } from "../../components/RecordHeader";
import { Loader } from "../../components/Loader";
import { StatusPill, toneTextClass } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
// The client mirror of the server's canonical LIFT_TYPE_LABELS (server/src/modules/quotes/liftSpec.ts),
// already parity-tested byte-for-byte against it (quoteFunnel.parity.test.ts). Reused here rather than
// duplicated: the client SPA cannot import server/src at runtime (a separate Vite bundle), so this is
// the same "two homes, parity tested" pattern the platform already uses for the duration rule.
import { LIFT_TYPE_LABELS_CLIENT } from "../../lib/quoteFunnel";
import { MANUFACTURER_SUGGESTIONS } from "../../lib/quotePricing";
// Session AMF: the Condition Report trigger (the rule) and its prompt (its face).
import { conditionReportTrigger } from "../../lib/conditionReportTrigger";
import { ConditionReportPrompt } from "./ConditionReportPrompt";

const sectionCls = "rounded-ele border border-ele-purple-accent bg-ele-surface p-5";
const h2Cls = "mb-3 font-heading text-lg text-ele-blue";
const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const subHeadCls = "mb-2 font-menu text-xs uppercase tracking-wider text-ele-blue";
// Membership check for the Lift type select: is a stored value one of the canonical labels.
const LIFT_TYPE_LABEL_SET = new Set(Object.values(LIFT_TYPE_LABELS_CLIENT));
// Customer-facing tier label (the same map the office and accept-page surfaces use). ContractTier is
// core/care/complete; the internal engine key "cover" for the middle tier never reaches this DTO.
const TIER_LABEL: Record<string, string> = { core: "Core", care: "Care", complete: "Complete" };

// A read-only key/value row in a details list. A missing value reads "Not recorded", never blank.
function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  const text = value === null || value === undefined || value === "" ? "Not recorded" : String(value);
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ele-body">{label}</dt>
      <dd className="text-right text-ele-strong">{text}</dd>
    </div>
  );
}

// Session ADE: a record's VIEW expands the shared service-record panel INLINE on this page (no
// jump to the Service Records page). A call-out record keeps a secondary link to the Call-outs
// page, where the call-out-level actions (charge, intake invoice) live.
function calloutHref(r: ServiceHistoryItem): string {
  return `/office/callouts?job=${encodeURIComponent(r.id)}`;
}

interface EditForm {
  siteId: string;
  eleNumber: string;
  assetClass: string;
  liftType: string;
  frequency: string;
  manufacturer: string;
  serial: string;
  yearOfManufacture: string;
  stops: string;
  capacityKg: string;
  driveType: string;
  manualDoors: string; // "yes" | "no"
  simNumber: string;
  simExpiry: string;
  lolerDue: string;
  notes: string;
}

function toEditForm(a: Asset): EditForm {
  const d = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : "");
  return {
    siteId: a.siteId ?? "",
    eleNumber: a.eleNumber ?? "",
    assetClass: a.assetClass ?? "lift",
    liftType: a.liftType ?? "",
    frequency: a.frequency ?? "",
    manufacturer: a.manufacturer ?? "",
    serial: a.serial ?? "",
    yearOfManufacture: a.yearOfManufacture != null ? String(a.yearOfManufacture) : "",
    stops: a.stops != null ? String(a.stops) : "",
    capacityKg: a.capacityKg != null ? String(a.capacityKg) : "",
    driveType: a.driveType ?? "",
    manualDoors: a.manualDoors ? "yes" : "no",
    simNumber: a.simNumber ?? "",
    simExpiry: d(a.simExpiry),
    lolerDue: d(a.lolerDue),
    notes: a.notes ?? "",
  };
}

export function LiftDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "office";

  // Session ADE fix: the setup must RE-ARM the ref, not just the initialiser. Under React
  // StrictMode's dev mount -> cleanup -> remount, a cleanup-only effect left this permanently
  // false, so every child list silently discarded its loaded data in dev (prod was unaffected).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const [asset, setAsset] = useState<Asset | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");
  const [serviceRecords, setServiceRecords] = useState<ServiceHistoryItem[]>([]);
  const [serviceTotal, setServiceTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lolerReports, setLolerReports] = useState<LolerReportListItem[]>([]);
  // Session ADI: the lift's own defects (a required FK, so the query is direct). Listed in the
  // Defects section, each linking to /office/defects/:id.
  const [defects, setDefects] = useState<DefectListItem[]>([]);
  const [safedTests, setSafedTests] = useState<SafedTestItem[]>([]);
  const [certificates, setCertificates] = useState<AssetCertificateItem[]>([]);
  const [photos, setPhotos] = useState<AssetPhotoHistoryItem[]>([]);
  const [parts, setParts] = useState<AssetPartItem[]>([]);

  // Session AMF: what to say after a Condition Report is raised from the prompt below. This page
  // has no general notice area of its own, so the message gets a small one of its own rather than
  // being dropped (a raise that reports nothing reads as a raise that did nothing).
  const [raiseNotice, setRaiseNotice] = useState<string | null>(null);

  const refreshChildren = useCallback(() => {
    const m = mountedRef;
    api.assets.serviceHistory(id, { limit: 5 }).then((r) => { if (m.current) { setServiceRecords(r.items); setServiceTotal(r.total); } }).catch(() => m.current && setServiceRecords([]));
    api.loler.reportsForAsset(id).then((r) => m.current && setLolerReports(r)).catch(() => m.current && setLolerReports([]));
    api.defects.list("all", id).then((r) => m.current && setDefects(r)).catch(() => m.current && setDefects([]));
    api.assets.safedTests(id).then((r) => m.current && setSafedTests(r)).catch(() => m.current && setSafedTests([]));
    api.assets.certificates(id).then((r) => m.current && setCertificates(r)).catch(() => m.current && setCertificates([]));
    api.assets.photoHistory(id).then((r) => m.current && setPhotos(r)).catch(() => m.current && setPhotos([]));
    api.assets.parts(id).then((r) => m.current && setParts(r.items)).catch(() => m.current && setParts([]));
  }, [id]);

  useEffect(() => {
    let active = true;
    setState("loading");
    api.assets
      .get(id)
      .then((a) => {
        if (!active) return;
        setAsset(a);
        setState("ready");
        refreshChildren();
      })
      .catch((err) => {
        if (active) setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      });
    return () => {
      active = false;
    };
  }, [id, refreshChildren]);

  // Reload after any mutation (a child action component's onChanged, or the edit form save).
  const reload = useCallback(() => {
    const m = mountedRef;
    api.assets.get(id).then((a) => m.current && setAsset(a)).catch(() => {});
    refreshChildren();
  }, [id, refreshChildren]);

  // Expand the recent-records list (default 5) to this lift's full history, paging through the
  // per-asset service-history endpoint -- so the page stays scoped to THIS lift, not the whole site.
  function showAllServiceRecords() {
    setLoadingMore(true);
    api.assets
      .serviceHistory(id, { limit: serviceTotal })
      .then((r) => {
        if (mountedRef.current) {
          setServiceRecords(r.items);
          setServiceTotal(r.total);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) setLoadingMore(false);
      });
  }

  // Record a scheduled visit as completed on its real past date (Session AEB): the row the
  // office is entering, or null. The modal is self-sufficient (fetches the checklist template
  // and the engineer list by job id); on success the page reloads so the row shows completed.
  const [backdatedJob, setBackdatedJob] = useState<ServiceHistoryItem | null>(null);
  // Session AES: the same modal opened against the LIFT, for a visit that was never scheduled.
  const [retrospectiveVisit, setRetrospectiveVisit] = useState(false);

  // ---- Inline service-record expand (Session ADE) ----
  // VIEW on a record expands the shared ServiceRecordPanel in place (the same inline pattern as
  // the lift-details EDIT), instead of jumping to the Service Records page. Collapsing while an
  // amendment is unsaved asks before discarding (the panel guards route navigation itself).
  const [openRecordId, setOpenRecordId] = useState<string | null>(null);
  const recordDirtyRef = useRef(false);
  const [pendingCollapse, setPendingCollapse] = useState<(() => void) | null>(null);
  function toggleRecord(id: string) {
    if (openRecordId === id) {
      const collapse = () => setOpenRecordId(null);
      if (recordDirtyRef.current) setPendingCollapse(() => collapse);
      else collapse();
    } else {
      const expand = () => { recordDirtyRef.current = false; setOpenRecordId(id); };
      if (openRecordId != null && recordDirtyRef.current) setPendingCollapse(() => expand);
      else expand();
    }
  }

  // ---- Edit lift details (inline form) ----
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  function startEdit() {
    if (!asset) return;
    setForm(toEditForm(asset));
    setEditError(null);
    setEditing(true);
  }

  async function saveEdit() {
    if (!asset || !form) return;
    if (!form.siteId) {
      setEditError("Choose a site.");
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      // Strings flow through the server's optional* coercions; manualDoors is a real boolean and
      // driveType a select value (blank clears it).
      const updated = await assets.update(asset.id, {
        siteId: form.siteId,
        eleNumber: form.eleNumber,
        assetClass: form.assetClass,
        liftType: form.liftType,
        frequency: form.frequency,
        manufacturer: form.manufacturer,
        serial: form.serial,
        yearOfManufacture: form.yearOfManufacture,
        stops: form.stops,
        capacityKg: form.capacityKg,
        driveType: form.driveType,
        manualDoors: form.manualDoors === "yes",
        simNumber: form.simNumber,
        simExpiry: form.simExpiry,
        lolerDue: form.lolerDue,
        notes: form.notes,
      });
      // PATCH returns the bare row (no computed fields); keep the computed status from the loaded
      // asset and reload in the background to refresh everything.
      setAsset((prev) => (prev ? { ...prev, ...updated } : updated));
      setEditing(false);
      reload();
    } catch (err) {
      // A 400 validation error carries per-field messages in err.details (no top-level message);
      // surface those rather than the raw "invalid_request" code.
      if (err instanceof ApiError && err.details) {
        setEditError(
          Object.entries(err.details)
            .map(([field, msgs]) => `${field}: ${(msgs as string[]).join(", ")}`)
            .join("; "),
        );
      } else {
        setEditError(err instanceof ApiError ? err.message || err.code : "Could not save the lift. Please check the fields and try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  // ---- Delete a part record (admin/office; the server blocks a part on an approved job sheet) ----
  const [partError, setPartError] = useState<string | null>(null);
  async function deletePart(partId: string) {
    if (!asset) return;
    setPartError(null);
    try {
      await api.assets.deletePart(asset.id, partId);
      refreshChildren();
    } catch (err) {
      setPartError(err instanceof ApiError ? err.message || "Could not delete the part. Please try again." : "Could not delete the part. Please try again.");
    }
  }

  // ---- Delete (admin/office; the server blocks with a 409 + blocker list) ----
  const [showDelete, setShowDelete] = useState(false);
  const [addLoler, setAddLoler] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function confirmDelete() {
    if (!asset) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await assets.remove(asset.id);
      navigate("/office/lifts");
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message || err.code : "Could not delete the lift. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  const eleLabel = asset?.eleNumber || asset?.legacyEleNumber || "Lift";

  // The LOLER next-due colour (90-day window), shown alongside the LOLER section's due date.
  const lolerBand = useMemo(() => (asset ? dueDateTone(asset.lolerDue, new Date()) : null), [asset]);
  // Open-defect count for the Defects section header (Session ADI).
  const openDefectCount = useMemo(() => defects.filter((d) => d.status === "open").length, [defects]);

  // Session AMF: does this lift need a Full Condition Report before ELE quotes or services it?
  // The rule is pure and shared (src/lib/conditionReportTrigger.ts); everything it reads is
  // already loaded here, so this costs no extra request. `jobsComplete` is honest about the
  // default 5-record window: with a longer history than that, "no condition report visible" is not
  // proof there is none -- and pressing Raise anyway is safe, because the route reuses an open one
  // rather than raising a second GBP 510 charge.
  const conditionReport = useMemo(
    () =>
      conditionReportTrigger({
        assetClass: asset?.assetClass,
        hasContract: asset?.hasContract,
        retiredAt: asset?.retiredAt,
        lastServicedAt: asset?.lastServicedAt,
        jobs: serviceRecords,
        jobsComplete: serviceRecords.length >= serviceTotal,
      }),
    [asset, serviceRecords, serviceTotal],
  );

  return (
    <div>
      {!asset && (
        <Link to="/office/lifts" className="font-menu text-xs tracking-wide text-ele-green hover:underline">
          Back to Lifts and equipment
        </Link>
      )}

      {state === "loading" ? (
        <Loader shape="panel" className="mt-4" />
      ) : state === "notfound" ? (
        <p className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          That lift could not be found.
        </p>
      ) : state === "error" ? (
        <p role="alert" className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load this lift. Please try again in a moment.
        </p>
      ) : asset ? (
        <>
          {/* Design system Session 5: the shared record header (spec 3.1). The status dot sits beside the
              title; the site and customer are its subtitle; Edit and Delete are its actions. */}
          <RecordHeader
            className="mb-5 mt-2"
            backTo="/office/lifts"
            backLabel="Back to Lifts and equipment"
            title={eleLabel}
            status={<LiftStatusDot asset={asset} />}
            subtitle={
              <>
                {asset.site?.id ? (
                  <Link to={`/office/sites/${encodeURIComponent(asset.site.id)}`} className="text-ele-green underline-offset-2 hover:underline">
                    {asset.site.name}
                  </Link>
                ) : (
                  asset.site?.name ?? "Unassigned site"
                )}
                {asset.site?.address ? `, ${asset.site.address}` : ""}
                {asset.site?.postcode ? `, ${asset.site.postcode}` : ""}
                {asset.site?.customer && (
                  <span className="mt-0.5 block">
                    <Link
                      to={`/office/customers/${encodeURIComponent(asset.site.customer.id)}`}
                      className="text-ele-green underline-offset-2 hover:underline"
                    >
                      {asset.site.customer.name}
                    </Link>
                  </span>
                )}
              </>
            }
            actions={
              canEdit ? (
                <>
                  {/* Session ASM (W-12): raise work for THIS lift through the ordinary flows, pre-filled. Not on a
                    retired lift, which takes no new work. */}
                {!asset.retiredAt && (
                  <RaiseWorkMenu
                    options={["callout", "repair", "quote"]}
                    context={{
                      assetId: asset.id,
                      siteId: asset.site?.id ?? null,
                      siteName: asset.site?.name ?? null,
                      siteAddress: asset.site?.address ?? null,
                      customerId: asset.site?.customer?.id ?? null,
                      customerName: asset.site?.customer?.name ?? null,
                    }}
                  />
                )}
                {!asset.retiredAt && <RaiseSafedButton assetId={asset.id} onRaised={reload} />}
                  <Button variant="secondary" size="sm" onClick={editing ? () => setEditing(false) : startEdit}>
                    {editing ? "Close edit" : "Edit"}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => { setDeleteError(null); setShowDelete(true); }}>
                    Delete
                  </Button>
                </>
              ) : undefined
            }
          />

          {/* Session AMF: the Condition Report trigger. Renders nothing at all unless this lift
              genuinely has no documented routine maintenance on file (conditionReportTrigger), so
              it is invisible on every ordinary contracted lift. Placed directly under the header,
              above every section, because it is a question about whether ELE should be quoting or
              servicing this lift at all -- it belongs before the detail, not buried among it. */}
          {conditionReport.required && (
            <ConditionReportPrompt
              trigger={conditionReport}
              assetId={asset.id}
              liftRef={asset.eleNumber ?? asset.legacyEleNumber ?? null}
              onRaised={(message) => {
                setRaiseNotice(message);
                reload();
              }}
            />
          )}
          {raiseNotice && (
            <p role="status" className="mb-4 rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {raiseNotice}
            </p>
          )}

          <div className="space-y-5">
            {/* Lift details */}
            <section className={sectionCls}>
              <h2 className={h2Cls}>Lift details</h2>
              {editing && form ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="block">
                    <RecordPicker
                      kind="site"
                      label="Site"
                      value={form.siteId}
                      selectedLabel={form.siteId && form.siteId === asset.site?.id ? asset.site?.name : undefined}
                      placeholder="Search for a site"
                      onChange={(id) => setForm({ ...form, siteId: id })}
                    />
                  </div>
                  <label className="block">
                    <span className={labelCls}>Lift ID (ELE number)</span>
                    <input value={form.eleNumber} onChange={(e) => setForm({ ...form, eleNumber: e.target.value })} className={inputCls} />
                  </label>
                  <label className="block">
                    {/* "Asset class", matching the read view: three type-ish fields live on this
                        form (class / lift type / drive type) and each wears its own name so none
                        is ever conflated with another (Session ADZ). */}
                    <span className={labelCls}>Asset class</span>
                    <select value={form.assetClass} onChange={(e) => setForm({ ...form, assetClass: e.target.value })} className={inputCls}>
                      {ASSET_KIND_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className={labelCls}>Lift type</span>
                    <select value={form.liftType} onChange={(e) => setForm({ ...form, liftType: e.target.value })} className={inputCls}>
                      <option value="">Not set</option>
                      {/* A legacy or hand-typed value that does not match a canonical label (e.g. ELE-078A)
                          is kept visible as its own option, rather than the select silently showing blank,
                          so the office can see what is actually stored before picking the correct one. */}
                      {form.liftType && !LIFT_TYPE_LABEL_SET.has(form.liftType) && (
                        <option value={form.liftType}>{form.liftType} (not a standard type, choose one below)</option>
                      )}
                      {Object.values(LIFT_TYPE_LABELS_CLIENT).map((label) => (
                        <option key={label} value={label}>{label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className={labelCls}>Service frequency</span>
                    <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className={inputCls}>
                      {FREQUENCY.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className={labelCls}>Manufacturer</span>
                    <input
                      value={form.manufacturer}
                      onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                      list="lift-manufacturer-suggestions"
                      className={inputCls}
                    />
                    {/* Same convenience list the quote calculator offers (src/lib/quotePricing.ts); a
                        datalist never restricts input, so an off-list manufacturer still saves as typed. */}
                    <datalist id="lift-manufacturer-suggestions">
                      {MANUFACTURER_SUGGESTIONS.map((m) => (
                        <option key={m} value={m} />
                      ))}
                    </datalist>
                  </label>
                  <label className="block">
                    <span className={labelCls}>Serial number</span>
                    <input value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Year of manufacture</span>
                    <input type="number" value={form.yearOfManufacture} onChange={(e) => setForm({ ...form, yearOfManufacture: e.target.value })} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Capacity (kg)</span>
                    <input type="number" value={form.capacityKg} onChange={(e) => setForm({ ...form, capacityKg: e.target.value })} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Number of stops</span>
                    <input type="number" value={form.stops} onChange={(e) => setForm({ ...form, stops: e.target.value })} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Drive type</span>
                    <select value={form.driveType} onChange={(e) => setForm({ ...form, driveType: e.target.value })} className={inputCls}>
                      {DRIVE_TYPE.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className={labelCls}>Manual doors</span>
                    <select value={form.manualDoors} onChange={(e) => setForm({ ...form, manualDoors: e.target.value })} className={inputCls}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className={labelCls}>SIM number</span>
                    <input value={form.simNumber} onChange={(e) => setForm({ ...form, simNumber: e.target.value })} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>SIM expiry</span>
                    <input type="date" value={form.simExpiry} onChange={(e) => setForm({ ...form, simExpiry: e.target.value })} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>LOLER due</span>
                    <input type="date" value={form.lolerDue} onChange={(e) => setForm({ ...form, lolerDue: e.target.value })} className={inputCls} />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className={labelCls}>Notes</span>
                    <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={inputCls} />
                  </label>
                  {editError && <p role="alert" className="text-sm text-ele-danger sm:col-span-2">{editError}</p>}
                  <div className="flex gap-2 sm:col-span-2">
                    <Button size="sm" onClick={saveEdit} disabled={saving}>
                      {saving ? "Saving..." : "Save changes"}
                    </Button>
                    <button onClick={() => setEditing(false)} disabled={saving} className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                  <Field label="Lift type" value={assetTypeLabel(asset)} />
                  <Field label="Manufacturer" value={asset.manufacturer} />
                  <Field label="Serial number" value={asset.serial} />
                  <Field label="Year of manufacture" value={asset.yearOfManufacture} />
                  <Field label="Capacity (kg)" value={asset.capacityKg} />
                  <Field label="Number of stops" value={asset.stops} />
                  <Field label="Drive type" value={asset.driveType ? valueToLabel(DRIVE_TYPE, asset.driveType) : null} />
                  <Field label="Asset class" value={valueToLabel(ASSET_KIND_OPTIONS, asset.assetClass)} />
                  <Field label="Manual doors" value={asset.manualDoors ? "Yes" : "No"} />
                  {/* Session AGD: the maintenance tier this lift is covered on, so the office sees
                      the service level at a glance without navigating away. Read-only (the tier is
                      set on the contract, not the lift). Its own full-width row between the type
                      fields and the SIM fields, keeping SIM number / SIM expiry paired. */}
                  <div className="flex justify-between gap-3 sm:col-span-2">
                    <dt className="text-ele-body">Maintenance tier</dt>
                    <dd className="text-right">
                      {asset.contractTier ? (
                        // Session AQW, item C-4: link through to the covering contract when the
                        // server could resolve one id (it always can for a migrated lift's site
                        // fallback; see asset.routes.ts). A pill with no id left un-linked, rather
                        // than a link to a contract the server did not actually name.
                        asset.contractId ? (
                          <Link to={`/office/contracts/${encodeURIComponent(asset.contractId)}`} className="no-underline hover:underline">
                            <StatusPill word={TIER_LABEL[asset.contractTier] ?? asset.contractTier} tone="grey" />
                          </Link>
                        ) : (
                          <StatusPill word={TIER_LABEL[asset.contractTier] ?? asset.contractTier} tone="grey" />
                        )
                      ) : (
                        <span className="text-ele-body opacity-70">No active contract</span>
                      )}
                    </dd>
                  </div>
                  <Field label="SIM number" value={asset.simNumber} />
                  <Field label="SIM expiry" value={asset.simExpiry ? formatDate(asset.simExpiry) : null} />
                </dl>
              )}
              {!editing && asset.notes && asset.notes.trim() && (
                <p className="mt-3 border-t border-ele-purple-accent pt-3 text-sm text-ele-body">{asset.notes}</p>
              )}
            </section>

            {/* Service (Session AQW, item C-10: moved up from below the three tag sections. The
                office's most common reason to open this page is checking the visit history, so it
                now follows Lift details directly. RFID tag / Guv POP Tag / POP Tag history move
                down together as a group, unchanged relative to each other.) */}
            <section className={sectionCls}>
              <h2 className={h2Cls}>Service</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <span className={labelCls}>Last serviced</span>
                  <LastServicedButton asset={asset} onChanged={reload} hideProjection detail />
                </div>
                <div>
                  <span className={labelCls}>Next scheduled service</span>
                  <div className="text-sm"><NextServiceDueCell asset={asset} /></div>
                  <div className="mt-1 text-sm text-ele-body">Frequency: {valueToLabel(FREQUENCY, asset.frequency)}</div>
                  <div className="mt-2"><GeneratePpmButton asset={asset} onChanged={reload} /></div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                <h3 className={`mb-0 ${subHeadCls}`}>Recent service records</h3>
                {/* Session AES: the visit that was never on the schedule. "Record as completed"
                    on a row above covers a planned visit nobody entered; this covers work
                    carried out before the lift was being scheduled here, or a visit that never
                    reached the calendar. Same modal, same checklist, same approval gate. */}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setRetrospectiveVisit(true)}
                    className="font-menu text-xs tracking-wide text-ele-blue hover:underline"
                  >
                    + Record a past visit
                  </button>
                )}
              </div>
              {serviceRecords.length === 0 ? (
                <p className="text-sm text-ele-body opacity-70">No service records on file for this lift yet.</p>
              ) : (
                <ul className="space-y-2">
                  {serviceRecords.map((r) => {
                    const open = openRecordId === r.id;
                    return (
                      <li key={r.id} className="rounded-ele border border-ele-purple-accent bg-ele-surface text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <JobTypeBadge type={r.type} size="sm" />
                            <span className="text-ele-strong">{r.dateIso ? formatDate(r.dateIso) : "No date"}</span>
                            {r.engineerFirstName && <span className="text-ele-body">{r.engineerFirstName}</span>}
                            <span className="text-ele-body">{r.outcome}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* The Director's ask, verbatim: "there should still be something
                                there for me to click and add them". A scheduled (or opened but
                                unrecorded) service visit offers the backdated entry right on its
                                row (Session AEB). */}
                            {canEdit && r.type === "service" && (r.status === "scheduled" || r.status === "in_progress") && (
                              <button
                                type="button"
                                onClick={() => setBackdatedJob(r)}
                                className="font-menu text-xs tracking-wide text-ele-blue hover:underline"
                              >
                                Record as completed
                              </button>
                            )}
                            {r.type === "callout" && (
                              <Link to={calloutHref(r)} className="font-menu text-xs tracking-wide text-ele-blue hover:underline">
                                Open on Call-outs page
                              </Link>
                            )}
                            <button
                              type="button"
                              onClick={() => toggleRecord(r.id)}
                              className="font-menu text-xs tracking-wide text-ele-green hover:underline"
                            >
                              {open ? "Hide" : "View"}
                            </button>
                          </div>
                        </div>
                        {open && (
                          <div className="border-t border-ele-purple-accent px-3 py-3">
                            <ServiceRecordInline
                              jobId={r.id}
                              onChanged={refreshChildren}
                              onDirtyChange={(d) => { recordDirtyRef.current = d; }}
                            />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              <DiscardChangesDialog
                open={pendingCollapse != null}
                onKeep={() => setPendingCollapse(null)}
                onDiscard={() => {
                  recordDirtyRef.current = false;
                  pendingCollapse?.();
                  setPendingCollapse(null);
                }}
              />
              {backdatedJob && (
                <BackdatedCompletionModal
                  jobId={backdatedJob.id}
                  liftLabel={`${asset.eleNumber}${backdatedJob.dateIso ? ` | planned for ${formatDate(backdatedJob.dateIso)}` : ""}`}
                  scheduledForIso={backdatedJob.dateIso}
                  onClose={() => setBackdatedJob(null)}
                  onRecorded={reload}
                />
              )}
              {retrospectiveVisit && (
                <BackdatedCompletionModal
                  assetId={asset.id}
                  liftLabel={`${asset.eleNumber} | a visit that was never on the schedule`}
                  scheduledForIso={null}
                  onClose={() => setRetrospectiveVisit(false)}
                  onRecorded={reload}
                />
              )}
              {serviceTotal > serviceRecords.length && (
                <Button variant="secondary" size="sm" onClick={showAllServiceRecords} loading={loadingMore} className="mt-2">
                  {`Show all ${serviceTotal} service records`}
                </Button>
              )}
            </section>

            {/* RFID tag (Session AEA: moved up from the bottom of the page -- sending a tag URL
                is a frequent office action, so it sits right under the lift details, visible
                without scrolling; same component, same behaviour) */}
            <section className={sectionCls}>
              <h2 className={h2Cls}>RFID tag</h2>
              <p className="mb-3 text-sm text-ele-body">
                The RFID tag URL powers the customer service page. Generate it, send it to an engineer to programme the physical tag, or rotate it.
              </p>
              <RfidTagButton asset={asset} />
            </section>

            {/* The Guv POP Tag (Session AMZ as "Tapping For Tracking"): the lift's SECOND tag, directly beneath the
                first, because the office's most likely mistake with two NFC stickers on one lift is
                confusing them. Same control, same actions, switched by tagKind; the one line of copy
                under the heading exists to make the difference impossible to miss at a glance. This
                tag is INTERNAL: nothing about it ever reaches a customer surface. */}
            <section className={sectionCls}>
              <h2 className={h2Cls}>Guv POP Tag</h2>
              <p className="mb-3 text-sm text-ele-body">
                The Guv POP Tag ("Proof Of Presence") is the internal tag that goes with the paper log card in the
                motor room. It is separate from the customer RFID sticker above, and customers never see it.
              </p>
              <RfidTagButton asset={asset} tagKind="tap" />
            </section>

            {/* The attendance this lift's POP Tag has recorded, beneath the tag that records it. */}
            <section className={sectionCls}>
              <h2 className={h2Cls}>POP Tag history</h2>
              <AttendanceTapHistory assetId={asset.id} />
            </section>

            {/* Parts History (every part ever fitted to this lift, newest first) */}
            <section className={sectionCls}>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className={`mb-0 ${h2Cls}`}>Parts History</h2>
                {canEdit && <LogPartButton asset={asset} onChanged={reload} />}
              </div>
              {partError && <p role="alert" className="mb-2 text-sm text-ele-danger">{partError}</p>}
              {parts.length === 0 ? (
                <p className="text-sm text-ele-body opacity-70">No parts recorded for this lift yet.</p>
              ) : (
                <div data-safetynet className="overflow-x-auto rounded-ele border border-ele-purple-accent">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                      <tr>
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Part</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="max-md:hidden px-3 py-2">Job</th>
                        {canEdit && <th className="px-3 py-2 text-right">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {parts.map((p) => (
                        <tr key={p.id} className="border-t border-ele-purple-accent">
                          <td className="px-3 py-2 align-top text-ele-strong">{p.dateIso ? formatDate(p.dateIso) : "No date"}</td>
                          <td className="px-3 py-2 align-top">
                            {p.description}
                            {p.partCode ? <span className="ml-2 text-xs text-ele-body opacity-70">{p.partCode}</span> : null}
                          </td>
                          <td className="px-3 py-2 text-right align-top">{p.quantity}</td>
                          <td className="max-md:hidden px-3 py-2 align-top">
                            {p.jobId ? (
                              <Link to={`/office/jobsheets/${encodeURIComponent(p.jobId)}`} className="font-menu text-xs tracking-wide text-ele-green hover:underline">
                                {p.jobRef || "View"}
                              </Link>
                            ) : (
                              <span className="text-ele-body">{p.jobRef || ""}</span>
                            )}
                          </td>
                          {canEdit && (
                            <td className="px-3 py-2 text-right align-top">
                              <ConfirmButton
                                onConfirm={() => deletePart(p.id)}
                                label="Delete"
                                className="font-menu text-xs tracking-wide text-ele-danger hover:underline"
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* LOLER */}
            <section className={sectionCls}>
              <h2 className={h2Cls}>LOLER</h2>
              <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <Field label="Last examination" value={asset.lastLolerDate ? formatDate(asset.lastLolerDate) : null} />
                <div className="flex justify-between gap-3">
                  <dt className="text-ele-body">Next examination due</dt>
                  <dd className={`text-right ${lolerBand ? toneTextClass(lolerBand) : "text-ele-strong"}`}>
                    {asset.lolerDue ? formatDate(asset.lolerDue) : "Not recorded"}
                  </dd>
                </div>
                <Field label="Examining body" value={asset.lolerInspector} />
              </dl>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <UploadCertButton asset={asset} onChanged={reload} />
                {/* Session BAD: add an examination report from an email or a dropped PDF, no Drive step. */}
                <Button type="button" onClick={() => setAddLoler(true)}>Add LOLER report</Button>
                {/* Session BAQ: the examiner's report items for this lift, worked to closure. */}
                <Link to={`/office/loler?tab=to-close&assetId=${encodeURIComponent(asset.id)}`} className="font-menu text-xs tracking-wide text-ele-green hover:underline">
                  LOLER items to close
                </Link>
              </div>

              <h3 className={`mt-4 ${subHeadCls}`}>Examination reports</h3>
              {lolerReports.length === 0 ? (
                <p className="text-sm text-ele-body opacity-70">No examination reports on file for this lift yet.</p>
              ) : (
                <ul className="space-y-2">
                  {lolerReports.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-ele-strong">{r.examinationDate ? formatDate(r.examinationDate) : "No date"}</span>
                        {r.inspectorName && <span className="text-ele-body">{r.inspectorName}</span>}
                        <span className={r.openCount > 0 ? "text-amber-400" : "text-ele-green"}>
                          {r.openCount > 0 ? `${r.openCount} open ${r.openCount === 1 ? "item" : "items"}` : "No open items"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        {r.hasReportPdf && (
                          <a href={api.loler.reportPdfHref(r.id)} target="_blank" rel="noopener noreferrer" className="font-menu text-xs tracking-wide text-ele-green hover:underline">
                            Report PDF
                          </a>
                        )}
                        <Link to={`/office/loler/${r.id}`} className="font-menu text-xs tracking-wide text-ele-green hover:underline">
                          Open report
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <InsuranceItemsSection assetId={asset.id} liftLabel={asset.eleNumber ?? asset.legacyEleNumber ?? "this lift"} className={sectionCls} />

            {/* Defects (Session ADI): this lift's defects, each linking to its detail page. Closes
                the "lift page has no defects section" gap the ADG audit found (B7). */}
            <section className={sectionCls}>
              <h2 className={h2Cls}>
                Defects{openDefectCount > 0 ? ` (${openDefectCount} open)` : ""}
              </h2>
              {defects.length === 0 ? (
                <p className="text-sm text-ele-body opacity-70">No defects recorded for this lift.</p>
              ) : (
                <ul className="space-y-2">
                  {defects.map((d) => (
                    <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className={d.severity === "immediate" ? "text-ele-danger" : "text-amber-400"}>
                          {d.severity === "immediate" ? "Immediate" : "Advisory"}
                        </span>
                        <span className="min-w-0 break-words text-ele-strong">{d.description}</span>
                        <span className={d.status === "open" ? "text-ele-blue" : "text-ele-green"}>
                          {d.status === "open" ? "Open" : "Resolved"}
                        </span>
                      </div>
                      <Link to={`/office/defects/${encodeURIComponent(d.id)}`} className="font-menu text-xs tracking-wide text-ele-green hover:underline">
                        Open defect
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Compliance: SAFed + test certificates. Photos moved to their own section below
                (Session AID); a visual log was never a compliance artefact. */}
            <section className={sectionCls}>
              <h2 className={h2Cls}>Compliance</h2>

              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h3 className={subHeadCls}>SAFed supplementary tests</h3>
                <SafedTestsButton asset={asset} onChanged={reload} />
                <SafedJobsPanel assetId={asset.id} />
              </div>
              {safedTests.length === 0 ? (
                <p className="mb-4 text-sm text-ele-body opacity-70">No SAFed tests recorded for this lift yet.</p>
              ) : (
                <ul className="mb-4 space-y-2">
                  {safedTests.map((t) => (
                    <li key={t.id} className="rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-ele-strong">{t.testType}</span>
                        <StatusPill domain="serviceDue" status={safedDueStatus(t.status)} variant="solid" />
                      </div>
                      <div className="text-ele-body">
                        {t.lastTestDate ? `Last tested ${formatDate(t.lastTestDate)}` : "No last test recorded"}
                        {t.nextDueDate ? `, next due ${formatDate(t.nextDueDate)}` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-t border-ele-purple-accent pt-4">
                <h3 className={subHeadCls}>Test certificates</h3>
                <CertificatesButton asset={asset} onChanged={reload} />
              </div>
              {certificates.length === 0 ? (
                <p className="mb-4 text-sm text-ele-body opacity-70">No test certificates on file for this lift yet.</p>
              ) : (
                <ul className="mb-4 space-y-2">
                  {certificates.map((c) => (
                    <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm">
                      <div>
                        <span className="text-ele-strong">{c.certNumber}</span>
                        <div className="text-ele-body">{c.type}, {c.result}, tested {c.testDateDisplay}{c.nextTestDueDisplay ? `, next due ${c.nextTestDueDisplay}` : ""}</div>
                      </div>
                      <a href={api.assets.certificateDownloadHref(asset.id, c.id)} target="_blank" rel="noopener noreferrer" className="font-menu text-xs tracking-wide text-ele-green hover:underline">
                        Download
                      </a>
                    </li>
                  ))}
                </ul>
              )}

            </section>

            {/* Photos (Session AID). The two buckets are shown together, because "where are this
                lift's photos" is one question, but they are headed SEPARATELY and never merged: the
                standing collection is the lift's own visual log and is editable here, while the
                visit photos belong to their service records and are read-only from this page.
                Previously the visit photos sat under Compliance, which was the wrong home for a
                visual log and left no place to put the standing ones. */}
            <section className={sectionCls}>
              <h2 className={h2Cls}>Photos</h2>

              <h3 className={subHeadCls}>Lift photos</h3>
              <LiftPhotos assetId={asset.id} />

              <div className="mt-4 border-t border-ele-purple-accent pt-4">
                <h3 className={subHeadCls}>From service visits</h3>
                {photos.length === 0 ? (
                  <p className="text-sm text-ele-body opacity-70">No visit photos on record for this lift. These are captured by engineers during a visit.</p>
                ) : (
                  <div className="space-y-3">
                    {photos.map((p) => (
                      <div key={p.jobId}>
                        <p className="text-xs text-ele-body">{p.ref}{p.date ? `, ${formatDate(p.date)}` : ""}</p>
                        <JobPhotoGallery jobId={p.jobId} count={p.photoCount} label="" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* The Edit/Delete actions live in the page header (top-right) only. The duplicate
                bottom "Actions" section was removed (Session AEH, FIX-1 A5): the delete's permanence
                warning is carried by the ConfirmDeleteModal below. */}
          </div>

          {addLoler ? (
            <AddLolerReportModal
              assetId={asset.id}
              liftLabel={eleLabel}
              onClose={() => setAddLoler(false)}
              onDone={() => { setAddLoler(false); reload(); }}
            />
          ) : null}

          <ConfirmDeleteModal
            open={showDelete}
            title={`Delete ${eleLabel}?`}
            message="This permanently deletes the lift AND all of its records: jobs, call-outs, service records, defects, LOLER reports, SAFed tests, test certificates and logged parts. Invoices are kept. This cannot be undone."
            warning="Use this only for a test or mistaken lift."
            confirmLabel="Delete lift"
            busy={deleting}
            error={deleteError}
            onConfirm={confirmDelete}
            onCancel={() => { if (!deleting) { setShowDelete(false); setDeleteError(null); } }}
          />
        </>
      ) : null}
    </div>
  );
}

